import { useEffect, useId, useMemo, useRef, useState } from "react";
import { rankedFilter } from "../lib/search";
import { Popover } from "./Popover";

export interface SelectOption {
  value: string;
  label: string;
  // Secondary text shown after the label in the list (currency, date, amount).
  hint?: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  // Accessible name. Pass `labelledBy` instead when a visible <label> exists.
  label?: string;
  labelledBy?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  // Visual variants: full-width field, compact inline, or the dark hero card.
  tone?: "field" | "compact" | "inverse";
  // A filter appears once the list is long enough to be worth searching.
  // Set it explicitly to force the field on or off.
  searchable?: boolean;
}

const SEARCH_FROM = 8;

// A listbox built from a button and a list, because the native control cannot
// be styled: its popup ignores the page's type, spacing and colour entirely.
// Keyboard behaviour follows the ARIA listbox pattern — arrows and Home/End
// move, typing jumps, Enter picks, Escape closes and returns focus.
export function Select({
  value, options, onChange, label, labelledBy, placeholder = "Choose…",
  disabled = false, required = false, tone = "field", searchable,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const listID = useId();
  const selected = useMemo(() => options.find((option) => option.value === value), [options, value]);
  const withSearch = searchable ?? options.length >= SEARCH_FROM;

  // The label ranks above the hint, so an account is still findable by its
  // currency and a transaction by its amount, without those burying a row
  // whose name actually starts with what was typed.
  const shown = useMemo(
    () => rankedFilter(options, query, (option) => [option.label, option.hint ?? ""]),
    [options, query],
  );

  // Opening sets the active option; an effect must not cascade a render.
  function openList() {
    setQuery("");
    setActive(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function close(focusTrigger = true) {
    setOpen(false);
    setQuery("");
    if (focusTrigger) trigger.current?.focus();
  }

  function pick(index: number) {
    const option = shown[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    close();
  }

  function step(from: number, direction: 1 | -1): number {
    if (shown.length === 0) return 0;
    for (let offset = 1; offset <= shown.length; offset += 1) {
      const next = (from + direction * offset + shown.length * offset) % shown.length;
      if (!shown[next]?.disabled) return next;
    }
    return from;
  }

  function onListKey(event: React.KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); setActive((index) => step(index, 1)); return;
      case "ArrowUp": event.preventDefault(); setActive((index) => step(index, -1)); return;
      case "Home": event.preventDefault(); setActive(step(-1, 1)); return;
      case "End": event.preventDefault(); setActive(step(shown.length, -1)); return;
      case "Enter": case " ": event.preventDefault(); pick(active); return;
      case "Escape": event.preventDefault(); close(); return;
      case "Tab": close(false); return;
      default: break;
    }
    // With a filter present the keystrokes belong to it, not to typeahead.
    if (withSearch || event.key.length !== 1) return;
    // Typeahead: repeated keys inside a second extend the search term. The
    // event supplies the timestamp, so the handler reads no ambient clock.
    const now = event.timeStamp;
    typed.current.text = now - typed.current.at > 900 ? event.key : typed.current.text + event.key;
    typed.current.at = now;
    const needle = typed.current.text.toLowerCase();
    const found = shown.findIndex((option) => !option.disabled && option.label.toLowerCase().startsWith(needle));
    if (found >= 0) setActive(found);
  }

  return (
    <div className={`select select-${tone} ${open ? "open" : ""}`}>
      <button
        type="button" className="select-trigger" ref={trigger} disabled={disabled}
        role="combobox" aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? listID : undefined}
        aria-label={label} aria-labelledby={labelledBy} aria-required={required || undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); openList(); }
        }}
      >
        <span className={`select-value ${selected ? "" : "placeholder"}`}>{selected ? selected.label : placeholder}</span>
        {selected?.hint ? <span className="select-hint">{selected.hint}</span> : null}
        <span className="select-caret" aria-hidden="true">▾</span>
      </button>
      <Popover
        anchorRef={trigger} open={open} onDismiss={() => setOpen(false)} className="select-list"
        role={withSearch ? undefined : "listbox"} id={withSearch ? undefined : listID} label={withSearch ? undefined : label}
        labelledBy={withSearch ? undefined : labelledBy} autoFocus={!withSearch}
        activeDescendant={!withSearch && shown[active] ? `${listID}-${active}` : undefined}
        onKeyDown={withSearch ? undefined : onListKey}
      >
        {withSearch ? (
          <div className="select-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
              ref={search} type="search" value={query} autoFocus autoComplete="off"
              placeholder={`Search ${options.length} options`} aria-label={label ?? "Search options"}
              role="combobox" aria-expanded aria-controls={listID} aria-autocomplete="list"
              aria-activedescendant={shown[active] ? `${listID}-${active}` : undefined}
              onChange={(event) => { setQuery(event.target.value); setActive(0); }}
              onKeyDown={onListKey}
            />
          </div>
        ) : null}
        <div ref={list} className="select-options" role={withSearch ? "listbox" : undefined} id={withSearch ? listID : undefined} aria-label={withSearch ? label : undefined}>
          {shown.map((option, index) => (
            <div
              key={option.value} id={`${listID}-${index}`} role="option"
              className="select-option" data-active={index === active} aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              onMouseEnter={() => setActive(index)}
              onClick={() => pick(index)}
            >
              <span className="select-option-label">{option.label}</span>
              {option.hint ? <span className="select-option-hint">{option.hint}</span> : null}
              {option.value === value ? <span className="select-tick" aria-hidden="true">✓</span> : null}
            </div>
          ))}
          {shown.length === 0 ? (
            <p className="select-empty">{options.length === 0 ? "Nothing to choose yet." : `Nothing matches “${query.trim()}”.`}</p>
          ) : null}
        </div>
      </Popover>
    </div>
  );
}
